import { type KeyboardEvent, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import KEYS from '@rover/constants/src/platform/keys.constants';
import { Box, Image } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';

import { GALLERY_IMAGE_SIZE } from './constants';

type ImageWithShimmerProps = {
  src: string;
  caption?: string;
  /** Accessible name, taking precedence over the visible `caption`. */
  altText?: string;
  width?: number;
  height?: number;
  fluid?: boolean;
  onClick?: () => void;
  cached?: boolean;
  setCached?: (cached: boolean) => void;
  describedById?: string;
};
const ImageWithShimmer = ({
  src,
  caption,
  altText,
  width = parseInt(GALLERY_IMAGE_SIZE, 10),
  height = parseInt(GALLERY_IMAGE_SIZE, 10),
  fluid = false,
  onClick,
  cached,
  setCached,
  describedById,
}: ImageWithShimmerProps): JSX.Element => {
  const { t } = useLingui();
  const [loaded, setLoaded] = useState(cached ?? false);
  const clickable = !!onClick;

  const fillSize = { width: '100%', height: '100%' };
  const boxSize = fluid ? fillSize : { width, height };

  return (
    <Box
      position="relative"
      {...boxSize}
      top="0"
      left="0"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...(fluid ? { aspectRatio: '1' } : {}),
      }}
    >
      <Box
        data-testid="open-light-box"
        onClick={onClick}
        role={clickable ? 'button' : undefined}
        webProps={{
          'aria-describedby': describedById,
          tabIndex: clickable ? 0 : undefined,
          onKeyDown: clickable
            ? (e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined,
        }}
        {...(fluid ? fillSize : {})}
        sx={{ cursor: clickable ? 'pointer' : 'default' }}
      >
        <Image
          src={src}
          description={altText || caption || t`image`}
          resizeMethod={fluid ? 'cover' : 'contain'}
          {...boxSize}
          borderRadius="primary"
          sx={fluid ? fillSize : undefined}
          onLoad={() => {
            setLoaded(true);
            if (setCached) setCached(true);
          }}
        />
      </Box>

      {!loaded && (
        <Box
          {...boxSize}
          position="absolute"
          top="0"
          left="0"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingShimmer
            {...boxSize}
            borderRadius="primary"
            m="0x"
            data-testid="loading-shimmer"
          />
        </Box>
      )}
    </Box>
  );
};

export default ImageWithShimmer;
